/**
 * Screen/tab capture: optional audio + periodic screenshots from the shared video track.
 */

function pickAudioMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
  ];
  for (const t of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) {
      return t;
    }
  }
  return "audio/webm";
}

const MAX_CAPTURE_WIDTH = 1280;

/**
 * Grab one JPEG frame from a playing video element.
 * @param {HTMLVideoElement} video
 */
export function captureVideoFrameJpeg(video) {
  return new Promise((resolve, reject) => {
    if (!video?.videoWidth) {
      reject(new Error("Video not ready for capture."));
      return;
    }
    const scale = Math.min(1, MAX_CAPTURE_WIDTH / video.videoWidth);
    const w = Math.round(video.videoWidth * scale);
    const h = Math.round(video.videoHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvas not supported."));
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to encode screenshot."));
      },
      "image/jpeg",
      0.72
    );
  });
}

/**
 * @param {{
 *   screenshotIntervalSec?: number,
 *   onScreenshot?: (payload: { blob: Blob, elapsedSec: number, index: number }) => void | Promise<void>,
 *   onScreenshotError?: (err: Error) => void,
 * }} [options]
 */
export async function createScreenCaptureSession(options = {}) {
  const intervalSec = Math.max(
    2,
    Math.min(60, Number(options.screenshotIntervalSec) || 10)
  );
  const onScreenshot = options.onScreenshot;
  const onScreenshotError = options.onScreenshotError;

  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getDisplayMedia
  ) {
    throw new Error(
      "Screen sharing is not supported in this browser. Use Chrome or Edge on desktop."
    );
  }

  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: true,
  });

  const videoTracks = stream.getVideoTracks();
  const audioTracks = stream.getAudioTracks();
  if (!videoTracks.length) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error(
      "No video track. Share a tab or window so screenshots can be captured."
    );
  }

  const hasAudio = audioTracks.length > 0;
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  await video.play();

  let recorder = null;
  let mimeType = pickAudioMimeType();
  const audioChunks = [];

  if (hasAudio) {
    const audioStream = new MediaStream(audioTracks);
    mimeType = pickAudioMimeType();
    recorder = new MediaRecorder(audioStream, { mimeType });
    recorder.addEventListener("dataavailable", (ev) => {
      if (ev.data?.size > 0) audioChunks.push(ev.data);
    });
  }

  const startedAt = Date.now();
  let screenshotIndex = 0;
  let screenshotTimer = null;
  const screenshotQueue = [];
  let drainingQueue = false;

  const stopAllTracks = () => {
    if (screenshotTimer) {
      clearInterval(screenshotTimer);
      screenshotTimer = null;
    }
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    try {
      video.pause();
      video.srcObject = null;
    } catch {
      /* ignore */
    }
  };

  const runScreenshot = async () => {
    const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
    const blob = await captureVideoFrameJpeg(video);
    const payload = {
      blob,
      elapsedSec,
      index: screenshotIndex,
    };
    screenshotIndex += 1;
    if (onScreenshot) {
      await onScreenshot(payload);
    }
  };

  const drainScreenshotQueue = async () => {
    if (drainingQueue) return;
    drainingQueue = true;
    try {
      while (screenshotQueue.length) {
        const task = screenshotQueue.shift();
        try {
          await task();
        } catch (err) {
          onScreenshotError?.(
            err instanceof Error ? err : new Error(String(err))
          );
        }
      }
    } finally {
      drainingQueue = false;
    }
  };

  const enqueueScreenshot = () => {
    screenshotQueue.push(() => runScreenshot());
    drainScreenshotQueue();
  };

  return {
    stream,
    video,
    mimeType,
    hasAudio,
    screenshotIntervalSec: intervalSec,
    getScreenshotCount: () => screenshotIndex,

    start() {
      if (recorder) {
        audioChunks.length = 0;
        recorder.start(1000);
      }
      screenshotIndex = 0;
      window.setTimeout(() => {
        enqueueScreenshot();
      }, 800);
      screenshotTimer = window.setInterval(() => {
        enqueueScreenshot();
      }, intervalSec * 1000);
    },

    stop() {
      return new Promise((resolve, reject) => {
        if (screenshotTimer) {
          clearInterval(screenshotTimer);
          screenshotTimer = null;
        }

        const finish = () => {
          stopAllTracks();
          const blob =
            recorder && audioChunks.length
              ? new Blob(audioChunks, { type: mimeType })
              : new Blob([], { type: mimeType });
          resolve(blob);
        };

        const afterScreenshots = () => {
          if (!recorder || recorder.state === "inactive") {
            finish();
            return;
          }
          recorder.addEventListener("stop", finish, { once: true });
          recorder.addEventListener(
            "error",
            () => {
              stopAllTracks();
              reject(new Error("Audio recording failed."));
            },
            { once: true }
          );
          try {
            recorder.stop();
          } catch (e) {
            stopAllTracks();
            reject(e);
          }
        };

        drainScreenshotQueue().then(afterScreenshots).catch(afterScreenshots);
      });
    },
  };
}

export function formatRecordingDuration(ms) {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatTimestamp(sec) {
  const n = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
